import { Popover } from 'antd'
import CantileverButton from 'components/CantileverButton'
import {
  ChartControls,
  useOperationalEvents,
  useScatterplotContext,
  useTimeSeriesSensors,
} from 'features/timeSeries'
import { ScatterplotSidebarView } from 'features/timeSeries/components/ScatterplotSidebar'
import { ThresholdConfigurationModal } from 'features/timeSeries/components/ThresholdConfigurationModal'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { useInterpolatedHover } from 'features/timeSeries/hooks/useInterpolatedHover'
import { useSensorTableData } from 'features/timeSeries/utils/scatterPlotTableHelpers'
import { Layers } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { TimeSeriesScatterplotChart } from './TimeSeriesScatterplotChart'
import { useChartInteractionFunctions } from './useChartInteractionFunctions'
import { useMemoizedSensorValues } from './useMemoizedSensorValues'
import { usePlotlyGenerators } from './usePlotlyGenerators'

const ChartSettingsButton = styled(CantileverButton)`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
`

const PopoverContent = styled.div`
  width: 24rem;
  max-height: 70vh;
  overflow-y: auto;
  padding: 1rem;
`

interface TimeSeriesScatterplotContentProps {
  showChartSettings?: boolean
  unitID?: string
  unitName?: string
  sidebarView?: ScatterplotSidebarView
}

export const TimeSeriesScatterplotContent = React.forwardRef<
  HTMLDivElement,
  TimeSeriesScatterplotContentProps
>(({ showChartSettings = true, unitID, unitName, sidebarView }, ref) => {
  const [chartSettingsOpen, setChartSettingsOpen] = useState(false)
  const [showThresholdModal, setShowThresholdModal] = useState(false)
  const [shouldAnimateSpinner, setShouldAnimateSpinner] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState(false)

  // Fetch and transform work orders into operational events
  const { operationalEvents } = useOperationalEvents(unitID)
  const { clearCursorData, updateCursorData, cursorData } = useCursorData()
  const {
    chartData,
    isLoading,
    derivedValues,
    unitMapping,
    sensorRanges,
    localThresholdOverrides,
    setLocalThresholdOverrides,
    connectPoints,
    xAxisSensorId,
    customAxisRanges,
    dataLayers,
    setDataLayers,
    sensorVisibilityConfig,
    customSensorColors,
    sensorEntries,
    selectedSensors,
    setTimeRange,
    siteID,
    isResizing,
  } = useScatterplotContext()

  const tableData = useSensorTableData(
    selectedSensors,
    derivedValues,
    sensorRanges,
    customAxisRanges,
    cursorData,
    customSensorColors,
    xAxisSensorId,
    unitMapping,
    sensorEntries
  )

  const { enhancedSensorEntries, uniqueThresholds } = useTimeSeriesSensors({
    sensorEntries,
    selectedSensors,
    localThresholdOverrides,
  })

  const { isLiveTailRefresh, allSensorsToRender, selectedSensorEntries } =
    useMemoizedSensorValues({
      enhancedSensorEntries,
    })

  const { plotlyLayout, plotlyData, hasDataPoints } = usePlotlyGenerators({
    operationalEvents,
    enhancedSensorEntries,
    uniqueThresholds,
    allSensorsToRender,
  })

  // Keep animation running during fade-out transition
  // Show spinner if loading AND (not a live tail refresh OR it's the initial load with no data yet)
  const hasConfiguredData =
    selectedSensors.length > 0 || derivedValues.length > 0
  const isInitialLoad = chartData.length === 0 && hasConfiguredData
  const shouldShowSpinner = isLoading && (!isLiveTailRefresh || isInitialLoad)

  const {
    handleUnhover,
    handleRelayout,
    handleHover,
    handleConfigureThresholds,
    handleSaveThresholdConfigurations,
    toggleLayer,
    cursorPosition,
    setCursorPosition,
  } = useChartInteractionFunctions({
    clearCursorData,
    setTimeRange,
    allSensorsToRender,
    enhancedSensorEntries,
    updateCursorData,
    setShowThresholdModal,
    selectedSensors,
    unitID,
    unitName,
    siteID,
    setLocalThresholdOverrides,
    setDataLayers,
  })

  const { internalRef, handleMouseMove } = useInterpolatedHover({
    xAxisSensorId,
    connectPoints,
    chartData,
    allSensorsToRender,
    enhancedSensorEntries,
    unitMapping,
    updateCursorData,
    setCursorPosition,
  })

  // Show spinner if loading AND (not a live tail refresh OR it's the initial load with no data yet)
  useEffect(() => {
    if (shouldShowSpinner) {
      setShouldAnimateSpinner(true)
    } else {
      const timer = setTimeout(() => {
        setShouldAnimateSpinner(false)
      }, 200)

      return () => clearTimeout(timer)
    }
  }, [shouldShowSpinner, setShouldAnimateSpinner])

  // Debounce showing empty state to prevent flicker when data loads quickly
  useEffect(() => {
    if (!hasDataPoints && !isLoading) {
      const timer = setTimeout(() => {
        setShowEmptyState(true)
      }, 100)

      return () => clearTimeout(timer)
    } else {
      setShowEmptyState(false)
    }
  }, [hasDataPoints, isLoading, setShowEmptyState])

  // Auto-disable operational events layer when no events are available
  useEffect(() => {
    if (operationalEvents.length === 0) {
      setDataLayers((prev) => {
        const eventsLayer = prev.find((layer) => layer.id === 'events')
        if (eventsLayer?.enabled) {
          return prev.map((layer) =>
            layer.id === 'events' ? { ...layer, enabled: false } : layer
          )
        }
        return prev
      })
    }
  }, [operationalEvents.length, setDataLayers])

  return (
    <>
      {showChartSettings && (
        <Popover
          content={
            <PopoverContent>
              <ChartControls
                dataLayers={dataLayers}
                onToggleLayer={toggleLayer}
                operationalEventsCount={operationalEvents.length}
                handleConfigureThresholds={handleConfigureThresholds}
              />
            </PopoverContent>
          }
          trigger='click'
          open={chartSettingsOpen}
          onOpenChange={setChartSettingsOpen}
          placement='bottomLeft'
          styles={{ root: { width: '24rem' } }}>
          <ChartSettingsButton
            onClick={() => setChartSettingsOpen(!chartSettingsOpen)}>
            <Layers size={16} />
            Data Layers
          </ChartSettingsButton>
        </Popover>
      )}
      <TimeSeriesScatterplotChart
        ref={ref}
        cursorPosition={cursorPosition}
        derivedValues={derivedValues}
        handleHover={handleHover}
        handleMouseMove={handleMouseMove}
        handleRelayout={handleRelayout}
        handleUnhover={handleUnhover}
        hasDataPoints={hasDataPoints}
        internalRef={internalRef}
        isLoading={isLoading}
        isResizing={isResizing}
        plotlyData={plotlyData}
        plotlyLayout={plotlyLayout}
        selectedSensors={selectedSensors}
        sensorVisibilityConfig={sensorVisibilityConfig}
        shouldAnimateSpinner={shouldAnimateSpinner}
        shouldShowSpinner={shouldShowSpinner}
        showEmptyState={showEmptyState}
        sidebarView={sidebarView}
        tableData={tableData}
      />
      <ThresholdConfigurationModal
        open={showThresholdModal}
        onClose={() => setShowThresholdModal(false)}
        sensors={selectedSensorEntries}
        onSave={(thresholdConfigs) => {
          handleSaveThresholdConfigurations(thresholdConfigs)
        }}
      />
    </>
  )
})

TimeSeriesScatterplotContent.displayName = 'TimeSeriesScatterplotContent'
